
import React, { useEffect } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { useLanguage } from '@/contexts/LanguageContext';
import { translations } from '@/utils/translations';
import AccessibleBreadcrumb from '@/components/navigation/AccessibleBreadcrumb';
import LegalHeader from '@/components/legal/LegalHeader';
import LegalContent from '@/components/legal/LegalContent';
import { Card } from '@/components/ui/card';
import EnhancedPageTransition from '@/components/EnhancedPageTransition';

const LegalPage = () => {
  const { currentLanguage } = useLanguage();
  const t = translations[currentLanguage];

  // Define breadcrumb items based on current language
  const breadcrumbItems = [
    {
      label: t.legalTitle
    }
  ];
  
  // Focus management for keyboard navigation
  useEffect(() => {
    // Set focus to main content when page loads
    const mainContent = document.getElementById('main-content');
    if (mainContent) {
      mainContent.setAttribute('tabindex', '-1');
      mainContent.focus();
      // Remove outline after focus to avoid visual distraction
      mainContent.style.outline = 'none';
    }
    
    // Add page-specific structured data for Local Business
    const localBusinessData = {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "Future Level",
      "description": t.legalDescription,
      "url": window.location.href,
      "telephone": "+41 41 539 16 13",
      "email": "info@future-level.ch",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Zumhofstrasse",
        "addressLocality": "Kriens",
        "postalCode": "6010",
        "addressCountry": "CH"
      },
      "openingHoursSpecification": {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        "opens": "09:00",
        "closes": "17:00"
      },
      "sameAs": [
        "https://www.facebook.com/futurelevel",
        "https://www.instagram.com/futurelevel"
      ]
    };
    
    // Add the structured data to the page
    let scriptTag = document.getElementById('local-business-schema') as HTMLScriptElement | null;
    if (!scriptTag) {
      scriptTag = document.createElement('script');
      scriptTag.id = 'local-business-schema';
      scriptTag.type = 'application/ld+json';
      document.head.appendChild(scriptTag);
    }
    scriptTag.textContent = JSON.stringify(localBusinessData);
    
    // Add FAQ schema for improved SERP visibility
    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "What services does Future Level provide?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Future Level specializes in AI-generated marketing videos for businesses in Switzerland, including product videos, explainer videos, social media ads, and reels."
          }
        },
        {
          "@type": "Question",
          "name": "Where is Future Level located?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Future Level is located in Kriens (6010), Switzerland, but serves clients throughout Switzerland and beyond."
          }
        },
        {
          "@type": "Question",
          "name": "How can I contact Future Level?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "You can contact Future Level by email at info@future-level.ch or by phone at +41 41 539 16 13."
          }
        }
      ]
    };
    
    // Add FAQ schema to the page
    let faqScriptTag = document.getElementById('faq-schema') as HTMLScriptElement | null;
    if (!faqScriptTag) {
      faqScriptTag = document.createElement('script');
      faqScriptTag.id = 'faq-schema';
      faqScriptTag.type = 'application/ld+json';
      document.head.appendChild(faqScriptTag);
    }
    faqScriptTag.textContent = JSON.stringify(faqSchema);
    
    return () => {
      // Cleanup on unmount
      const script = document.getElementById('local-business-schema');
      if (script) {
        script.remove();
      }
      const faqScript = document.getElementById('faq-schema');
      if (faqScript) {
        faqScript.remove();
      }
    };
  }, [t]);

  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      <LegalHeader />
      <main 
        id="main-content" 
        className="flex-grow py-6 md:py-12 lg:py-16" 
        aria-label={t.legalTitle}
      >
        <EnhancedPageTransition variant="fade" duration={0.6}>
          <div className="container mx-auto px-4 lg:px-6">
            <AccessibleBreadcrumb items={breadcrumbItems} className="mb-6 md:mb-8" />
            <Card className="bg-black/20 backdrop-blur-sm border border-purple-500/20 p-0 overflow-hidden">
              <LegalContent />
            </Card>
          </div>
        </EnhancedPageTransition>
      </main>
      <Footer />
    </div>
  );
};

export default LegalPage;
